import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { 
  Building2, LayoutDashboard, Store, Landmark, Package, Receipt, 
  FileText, Target, BookOpen, Users, LogOut, Menu, X, 
  Sun, Moon, ChevronRight, Calculator, Shield, CreditCard, Truck
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import useTheme from '@/hooks/useTheme';
import useInactivityLogout from '@/hooks/useInactivityLogout';
import InactivityWarning from '@/components/InactivityWarning';

const Layout = ({ user, onLogout }) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { theme, toggleTheme, isDark } = useTheme();
  
  // Hook de inactividad - cierra sesión después de 15 minutos
  const { showWarning, secondsRemaining, continueSession, logout } = useInactivityLogout(onLogout);

  const navigation = [
    { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
    { name: 'Tiendas', path: '/tiendas', icon: Store },
    { name: 'Bancos', path: '/bancos', icon: Landmark },
    { name: 'Préstamos', path: '/prestamos', icon: CreditCard },
    { name: 'Proveedores', path: '/proveedores', icon: Truck },
    { name: 'Activos', path: '/activos', icon: Package },
    { name: 'Transacciones', path: '/transacciones', icon: Receipt },
    { name: 'Reportes', path: '/reportes', icon: FileText },
    { name: 'Presupuestos', path: '/presupuestos', icon: Target },
    { name: 'Cuentas', path: '/cuentas', icon: BookOpen },
    { name: 'Calculadora', path: '/calculadora', icon: Calculator },
    { name: 'Usuarios', path: '/usuarios', icon: Users },
    { name: 'Roles', path: '/roles', icon: Shield },
  ];

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-background transition-colors duration-300">
      {/* Mobile menu button */}
      <button
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 bg-slate-900 dark:bg-slate-700 text-white p-2.5 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
        data-testid="mobile-menu-toggle"
      >
        {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>

      {/* Theme toggle for mobile */}
      <button
        onClick={toggleTheme}
        className="lg:hidden fixed top-4 right-4 z-50 theme-toggle rounded-xl shadow-lg"
        data-testid="mobile-theme-toggle"
      >
        {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
      </button>

      {/* Overlay for mobile */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-20 transition-opacity duration-300"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 w-72 bg-gradient-to-b from-slate-900 via-slate-900 to-slate-800 dark:from-slate-950 dark:via-slate-950 dark:to-slate-900 shadow-2xl z-30 transform transition-all duration-300 ease-out ${
        mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      }`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center space-x-3 px-6 py-5 border-b border-slate-700/50">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-2.5 rounded-xl shadow-lg shadow-blue-500/25">
              <Building2 className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-white font-bold text-lg tracking-tight">Express Products</h1>
              <p className="text-slate-400 text-xs font-medium">Sistema Contable</p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1.5 overflow-y-auto">
            {navigation.map((item, index) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`group flex items-center justify-between px-4 py-3 rounded-xl transition-all duration-300 ${
                    active
                      ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/25'
                      : 'text-slate-400 hover:bg-slate-800/70 hover:text-white'
                  }`}
                  style={{ animationDelay: `${index * 50}ms` }}
                  data-testid={`nav-${item.name.toLowerCase()}`}
                >
                  <div className="flex items-center space-x-3">
                    <Icon className={`h-5 w-5 transition-transform duration-300 ${active ? '' : 'group-hover:scale-110'}`} />
                    <span className="font-medium text-sm">{item.name}</span>
                  </div>
                  {active && (
                    <ChevronRight className="h-4 w-4 opacity-70" />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Theme Toggle & User Info */}
          <div className="px-4 py-4 border-t border-slate-700/50 space-y-4">
            {/* Theme Toggle */}
            <div className="flex items-center justify-between px-2">
              <span className="text-sm text-slate-400 font-medium">Apariencia</span>
              <button
                onClick={toggleTheme}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800/50 hover:bg-slate-700/50 transition-all duration-300"
                data-testid="theme-toggle"
              >
                {isDark ? (
                  <>
                    <Moon className="h-4 w-4 text-blue-400" />
                    <span className="text-xs text-slate-300">Oscuro</span>
                  </>
                ) : (
                  <>
                    <Sun className="h-4 w-4 text-amber-400" />
                    <span className="text-xs text-slate-300">Claro</span>
                  </>
                )}
              </button>
            </div>

            {/* User Info */}
            <div className="bg-slate-800/30 rounded-xl p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
                  {user?.full_name?.charAt(0) || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{user?.full_name}</p>
                  <p className="text-xs text-slate-400 truncate">{user?.role || 'Usuario'}</p>
                </div>
              </div>
              <Button
                onClick={onLogout}
                variant="outline"
                className="w-full justify-center border-slate-600/50 text-slate-300 hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/50 transition-all duration-300"
                data-testid="logout-button"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Cerrar Sesión
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="lg:pl-72 transition-all duration-300">
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-4 sm:p-6 lg:p-8 pt-16 lg:pt-8">
          <Outlet />
        </div>
      </div>

      {/* Aviso de inactividad */}
      {showWarning && (
        <InactivityWarning
          secondsRemaining={secondsRemaining}
          onContinue={continueSession}
          onLogout={logout}
        />
      )}
    </div>
  );
};

export default Layout;
